import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";

// Eagerly load the landing page (most common entry point)
import Index from "./pages/Index";

// Lazy-load all other routes
const Admin = lazy(() => import("./pages/Admin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const TransactionDetail = lazy(() => import("./pages/TransactionDetail"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const Receipt = lazy(() => import("./pages/Receipt"));
const AgentProfile = lazy(() => import("./pages/AgentProfile"));
const AgentTransactionDetail = lazy(() => import("./pages/AgentTransactionDetail"));
const InspectionBooking = lazy(() => import("./pages/InspectionBooking"));
const UpdateFile = lazy(() => import("./pages/UpdateFile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Contact = lazy(() => import("./pages/Contact"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AdminAuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>}>
            <Routes>
              {/* Main Public Routes */}
              <Route path="/" element={<Index />} />
              <Route path="/get-started" element={<GetStarted />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              
              {/* Dashboard & File Routes */}
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/transaction/:id" element={<TransactionDetail />} />
              <Route path="/receipt/:id" element={<Receipt />} />
              <Route path="/agent/:token" element={<AgentProfile />} />
              <Route path="/agent/:token/transaction/:id" element={<AgentTransactionDetail />} />
              <Route path="/inspection/:token" element={<InspectionBooking />} />
              <Route path="/update-file" element={<UpdateFile />} />
              
              {/* 404 Fallback */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AdminAuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
